import React from "react";
import Reveal from "./Reveal";

/**
 * What This Movement Is Really About.
 *
 * Standalone section. Replaces the legacy "Expert Mirror" block
 * everywhere it used to render. Direct-address narrative inside
 * a soft charcoal panel with a faint gold edge-light so the prose
 * reads as editorial copy, not as gray text on raw black.
 *
 * The classNames bake the panel styling directly into JSX so the
 * design ships even when CSS tokens drift, third-party theme
 * extensions intercept utility classes, or browser extensions
 * strip aggressively.
 */
const WhatThisMovementIsAbout = ({ id = "what-this-is-about", showEyebrow = true }) => (
  <section
    id={id}
    data-testid="what-this-movement-section"
    className="relative py-24 md:py-32 border-t border-gold/10"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Reveal>
        <div
          data-testid="what-this-movement-panel"
          className="bg-white/5 border-t border-gold/20 p-8 rounded-lg border border-gold/15 ring-1 ring-gold/10 shadow-[0_0_60px_-30px_rgba(212,175,55,0.45)] backdrop-blur-sm sm:p-12 md:p-14 mx-auto max-w-4xl"
        >
          {showEyebrow ? (
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
              From the Founder
            </p>
          ) : null}
          <h2
            data-testid="what-this-movement-heading"
            className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight tracking-tight font-light max-w-3xl"
          >
            What This Movement Is{" "}
            <span className="italic gold-gradient-text">Really About.</span>
          </h2>

          <div
            data-testid="what-this-movement-body"
            className="mt-10 max-w-3xl space-y-6 text-white/85 leading-relaxed text-[16px] md:text-[17.5px]"
          >
            <p>Most people are not actually stuck.</p>
            <p>
              You are carrying too much at once. Too many half-decisions, too many borrowed strategies, too much noise stacked on top of the work that is actually yours to do.
            </p>
            <p>
              What you are calling stuckness is usually just the gap between what you already know and what you have given yourself permission to act on.
            </p>
            <p>Authentic Momentum is built to close that gap.</p>
            <p>
              Not by adding more frameworks to the pile. By helping you put the right ones down. By helping you name the signal underneath your friction. By helping you take the next move that actually fits where you are, in the timing that is honest for you.
            </p>
            <p>
              If something here meets you exactly where you are, that is the work doing what it is supposed to do.
            </p>
            <p className="pt-2">
              <a
                href="/framework"
                data-testid="movement-framework-link"
                className="text-[12px] tracking-[0.22em] uppercase text-gold hover:text-[#F3E5AB] underline-offset-[6px] hover:underline transition-colors"
              >
                Read the Framework
              </a>
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default WhatThisMovementIsAbout;
